import { useEffect, useState } from "react";
import { Box, Typography, Link } from "@mui/material";
import { RequestFieldDto } from "../Schemas/RequestFieldDto";
import { commonEmailDomains } from "../commonEmailDomains";
import { levenshtein, isValidEmailShape  } from "../Utilities/formUtils";

type Props = {
    field: RequestFieldDto;
    setValue: (value: string) => void;
};

const getBestDomainSuggestion = (domain: string): string | null => {
    let best: string | null = null;
    let bestScore = Infinity;

    for (const d of commonEmailDomains) {
        const score = levenshtein(domain, d);

        if (score < bestScore) {
            bestScore = score;
            best = d;
        }
    }

    return bestScore <= 2 ? best : null;
};

export const EmailSuggestionBox = ({
    field,
    setValue
}: Props) => {
    const value = field.paramValue ?? "";

    const [suggestion, setSuggestion] = useState<string | null>(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        setDismissed(false);

        if (!isValidEmailShape(value)) {
            setSuggestion(null);
            return;
        }

        const [local, domain] = value.split("@");

        if (!local || !domain) {
            setSuggestion(null);
            return;
        }

        const bestDomain = getBestDomainSuggestion(domain);

        if (bestDomain && bestDomain !== domain) {
            setSuggestion(`${local}@${bestDomain}`);
        } else {
            setSuggestion(null);
        }
    }, [value]);

    if (!suggestion || dismissed) return null;

    const applySuggestion = () => {
        setValue(suggestion);
        setDismissed(true);
    };

    const dismissSuggestion = () => {
        setDismissed(true);
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                width: "100%",
                gap: 2,
                mt: 0.5,
            }}
        >
            <Typography variant="body2" sx={{ flex: 1 }}>
                Zadali ste:{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                    {value}
                </Box>
                . Mali ste na mysli:{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                    {suggestion}
                </Box>
                ?
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
                <Link component="button" type="button" onClick={applySuggestion}>
                    Áno
                </Link>

                <Link component="button" type="button" onClick={dismissSuggestion}>
                    Nie
                </Link>
            </Box>
        </Box>
    );
};